import React, { ReactNode, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { IAppState } from 'infra/AppState'
import { pushEvent } from 'state/new-summary/events/events.actions'
import { newPaths } from 'routes'
import { NEWDASHBOARD_EVENTS } from 'state/new-summary/events/events.types'
import { useFeatureTags } from 'utils/featureTags'
import NewTag from 'components/NewTag'
import { getBusinessPrefs } from 'utils/businessPrefs'
import { TabsContainerStyles } from './styles'

const TabsContainer = ({
  tabs
}: {
  tabs: {
    icon: ReactNode
    title: string
    routeName: string
  }[]
}) => {
  const classes = TabsContainerStyles()
  return (
    <div className={classes.container}>
      {tabs.map((tab, index) => {
        return <IconTitle key={`tab_${tab.title}_icons_${index}`} tab={tab} />
      })}
    </div>
  )
}

const pathNameToEventMapper = {
  [newPaths.approve]: NEWDASHBOARD_EVENTS.APPROVE_TAB,
  [newPaths.supplyList]: NEWDASHBOARD_EVENTS.SUPPLY_TAB,
  [newPaths.collectionList]: NEWDASHBOARD_EVENTS.COLLECTION_TAB,
  [newPaths.summary]: NEWDASHBOARD_EVENTS.SUMMARY_TAB,
  [newPaths.ledger]: NEWDASHBOARD_EVENTS.LEDGER_TAB,
  [newPaths.reconcile]: NEWDASHBOARD_EVENTS.RECONCILE_TAB,
  [newPaths.billing]: NEWDASHBOARD_EVENTS.BILLING_TAB,
  [newPaths.chequeManagement]: NEWDASHBOARD_EVENTS.CHEQUE_TAB,
  [newPaths.settings]: NEWDASHBOARD_EVENTS.SETTINGS_TAB,
  [newPaths.manageAccounts]: NEWDASHBOARD_EVENTS.PROFILE_TAB,
  [newPaths.editMerchant]: NEWDASHBOARD_EVENTS.EDIT_MERCHANT,
  [newPaths.help]: NEWDASHBOARD_EVENTS.HELP_TAB
}

const IconTitle = ({
  tab
}: {
  tab: {
    icon: ReactNode
    title: string
    routeName: string
  }
}) => {
  const history = useHistory()
  const classes = TabsContainerStyles()
  const location = useLocation()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  // "New feature" tag (design mix A + D): pulse ring on first visit + NEW pill
  // until the feature is used — see utils/featureTags for the dismiss rules
  const featureTags = useFeatureTags()
  const businessId = useSelector((app: IAppState) => app.Dashboard.merchantProfile?.data?.id) || ''
  const isBulkReminderTab = tab.routeName === newPaths.collection

  // Android BulkCampaignEventTracker iconViewed/iconClicked: paid_status feeds from
  // observeHasAnyCampaignUserLevel — ANY campaign, paid or free (despite the name);
  // list_name = the list the campaign screen opens on (always ALL from the sidebar)
  const bulkIconProps = () => {
    const { bwr } = getBusinessPrefs(businessId)
    return { paid_status: !!(bwr.hasTriedCampaign || bwr.hasPaidCampaign), list_name: 'ALL' }
  }

  useEffect(() => {
    if (isBulkReminderTab) {
      dispatch(pushEvent(NEWDASHBOARD_EVENTS.BWR_ICON_VIEWED, bulkIconProps()))
    }
  }, [isBulkReminderTab])

  const tag =
    tab.routeName === newPaths.collection
      ? featureTags.collection
      : tab.routeName === newPaths.settings
      ? featureTags.settings
      : null

  const handleIconClick = (routeName: string) => {
    if (tag?.pulse) {
      tag.dismissPulse()
    }
    history.push(routeName)
    if (routeName === newPaths.collection) {
      dispatch(pushEvent(NEWDASHBOARD_EVENTS.BWR_ICON_CLICKED, bulkIconProps()))
    } else if (pathNameToEventMapper[routeName]) {
      dispatch(pushEvent(pathNameToEventMapper[routeName]))
    }
  }

  const isSelected = location?.pathname.split('/').at(2) === tab.routeName?.split('/').at(2)

  return (
    <div className={classes.iconTitleContainer} onClick={() => handleIconClick(tab.routeName)}>
      <div
        className={classNames(classes.iconContainer, {
          [classes.activeIconContainer]: isSelected
        })}
      >
        {tag?.pulse && <span className={classes.tabPulse} aria-hidden="true" />}
        {tab.icon}
        {tag?.isNew && <NewTag />}
      </div>
      <span
        className={classNames(classes.title, {
          [classes.activeText]: isSelected
        })}
      >
        {t(`dashboard:${tab.title}`)}
      </span>
    </div>
  )
}

export default TabsContainer
